/* eslint-disable @typescript-eslint/no-loop-func */
import type { DioramaState } from 'app/state/diorama/dioramaState';
import { openRewardChestPopup } from '../openRewardChestPopup';
import { dioramaConfigs } from '../../app/state/diorama/DioramaConfig';
import { DioramaFillPopup } from '../../game/views/popups/winFlow/DioramaFillPopup';
import { setContainerParent } from '../../utils/pixi/transform-utils';
import type { App } from '../../app/App';
import { exitToMainMenu } from '../exitToMainMenu';
import { DioramaView } from '../../game/views/dioramas/DioramaView';
import { getDioramaMissingTilesAmount } from '../../app/state/diorama/DioramaStateUtils';
import { DioramaSharePopup } from '../../game/views/popups/winFlow/DioramaSharePopup';
import { tryAddToFavourites } from '../tryAddToFavourites';
import { preloadLayoutsAssets } from '../../views/preloadLayoutsAssets';

export async function startDioramaFlow(app: App, score: number): Promise<void> {
  const { stateController, platform } = app;
  const { popups } = app.controller;
  let tilesLeft = score;

  let dioramaView: DioramaView | null = null;
  let newDioramaActivated = false;

  await popups.waitUntilNoActivePopups();

  popups.view.autoOverlayFading = false;
  popups.view.overlay.fadeIn();

  while ((tilesLeft > 0 || newDioramaActivated) && stateController.canAddProgressToDiorama()) {
    const dioramaState = stateController.getCurrentDioramaState();
    const { dioramaIndex, tilesAmount: currentTiles } = dioramaState;
    dioramaView = new DioramaView({
      config: dioramaConfigs[dioramaIndex],
      progress: currentTiles,
      isLocked: false,
    });

    const dioramaMissingTiles = getDioramaMissingTilesAmount(dioramaState);
    const willBeCompleted = tilesLeft >= dioramaMissingTiles;

    await openFillPopup(app, tilesLeft, dioramaView, stateController.getCurrentDioramaState(), newDioramaActivated);

    if (!willBeCompleted) setContainerParent(dioramaView, app.renderer.pixi.stage);

    await popups.closeActivePopup();

    if (willBeCompleted) {
      app.tracking.trackDioramaCompleted(dioramaIndex);
      app.resources.addDioramaCompletedReward(dioramaConfigs[dioramaIndex].reward);
      dioramaView = null;
      await openRewardChestPopup(
        app,
        {
          rewards: dioramaConfigs[dioramaIndex].reward,
        }
      );
      await popups.closeActivePopup();
      await openSharePopup(app, dioramaIndex);
      await popups.closeActivePopup();

    }

    stateController.increaseDioramaProgress(Math.min(tilesLeft, dioramaMissingTiles));
    tilesLeft -= Math.min(dioramaMissingTiles, tilesLeft);

    newDioramaActivated = stateController.getCurrentDioramaState().dioramaIndex !== dioramaIndex;
  }

  popups.view.autoOverlayFading = true;
  popups.closeAllPopups();
  await popups.waitUntilNoActivePopups();
  dioramaView?.showPieces();

  // FB can sometimes doesn't resolve shortcut promise so we don't wait
  void tryAddToFavourites({
    state: stateController,
    platform,
    levelNumber: stateController.getLevelNumber(),
  });

  void dioramaView?.hideBackground();
  exitToMainMenu(app, dioramaView ?? undefined);
}

async function openSharePopup(app: App, dioramaIndex: number): Promise<void> {
  const {
    platform: { shareable },
  } = app;

  if (!shareable) return Promise.resolve();

  if (!(await preloadLayoutsAssets(DioramaSharePopup.requiredLayouts, true))) return Promise.resolve();

  return new Promise((resolve) => {
    void app.controller.popups.addPopup({
      popupConstructor: DioramaSharePopup,
      data: dioramaConfigs[dioramaIndex],
      settings: {
        hideRays: true,
        clearStack: true,
      },
      onEvent: async (eventOrImage) => {
        if (eventOrImage === 'finished') {
          resolve();
          return;
        }
        void shareable.share?.(eventOrImage);
      },
    });
  });
}

function openFillPopup(app: App, score: number, dioramaView: DioramaView, dioramaState: DioramaState, isNewDiorama: boolean): Promise<void> {
  return new Promise((resolve) => {
    void app.controller.popups.addPopup({
      popupConstructor: DioramaFillPopup,
      data: { dioramaView, tilesAmount: score, dioramaState, isNewDiorama },
      settings: {
        hideRays: true,
        clearStack: true,
      },
      onEvent: async (event) => {
        if (event !== 'finished') return;
        resolve();
      },
    });
  });
}
